import { useState, useMemo } from 'react';
import { cv, UL } from '../utils/conversions';
import DollarInput from './DollarInput';

function AliasSection({aliases,onAdd,onRemove,t,si,sb}){
const[val,setVal]=useState('');
const[err,setErr]=useState('');
return<div style={{marginBottom:12,paddingTop:8,borderTop:`1px solid ${t.bd}`}}>
<div style={{fontSize:10,color:t.td,textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Aliases</div>
{aliases.length>0&&<div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
{aliases.map(a=><div key={a.id} style={{display:'flex',alignItems:'center',gap:4,padding:'3px 8px',background:t.sf,borderRadius:6,border:`1px solid ${t.bd}`,fontSize:11}}>
<span>{a.alias}</span>
{a.source&&<span style={{fontSize:8,color:t.td}}>({a.source})</span>}
<button onClick={()=>{setErr('');onRemove(a.id);}} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:10,padding:0}}>✕</button>
</div>)}
</div>}
<div style={{display:'flex',gap:6,alignItems:'center'}}>
<input value={val} onChange={e=>{setVal(e.target.value);setErr('');}} placeholder="Add alias (e.g. invoice wording)" style={{...si,flex:1,fontSize:11}}/>
<button onClick={async()=>{const r=await onAdd(val.trim());if(r==='duplicate')setErr('Already exists');else if(r==='empty')setErr('Enter an alias');else{setVal('');setErr('');}}} style={{...sb,background:t.bl,fontSize:10,padding:'4px 10px'}}>Add</button>
</div>
{err&&<div style={{fontSize:9,color:t.ac,marginTop:2}}>{err}</div>}
</div>;
}

const DISCRETE=new Set(['EACH','SLICE','DOZEN','SERVING']);
const BULK_UNITS=new Set(['OZ','LB','G','GAL','QUART','LITERS','ML','TBS','TSP']);
const EC_OPTS=["Food","Beverage","Liquor","Beer","Wine","Packaging","Supplies","Retail","Fees","Other"];
const EC_LABELS={Beverage:"NA Beverage"};
const PT_OPTS=["BOTTLE","PACK","BAG","JUG","CAN","EACH","BOX","TUB","CASE","JAR"];
const PDU_OPTS=["OZ","LB","SLICE","SERVING","EACH","GAL","QUART","LITERS","ML","G"];
const MIXED='__mixed__';
const SHAPE_KEYS=new Set(['ppc','spp','cu','pdu','srv','yp']);
const BULK_FIELDS=['cp','yp','cat','dist','ec','pt','cu','pdu','ppc','spp','srv'];

function computeUcFromState(s){
  const cpN=parseFloat(s.cp);if(!isFinite(cpN))return'';
  const srv=parseFloat(s.srv)||0;const ppc=parseFloat(s.ppc)||1;
  const spp=parseFloat(s.spp)||0;const yp=parseFloat(s.yp)||100;
  if(srv>0)return String(cpN/srv*(100/yp));
  if(s.pdu&&DISCRETE.has(s.pdu)&&BULK_UNITS.has(s.cu))return String(cpN/ppc*(100/yp));
  const cq=ppc*spp;if(!cq||!yp)return'';
  const base=cpN/cq*(100/yp);
  if(s.pdu&&s.pdu!==s.cu){const c=cv(1,s.pdu,s.cu);return String(c?base*c:base);}
  return String(base);
}

export default function IngredientForm({mode,initial,selectedIngs,onSave,onCancel,categories,vendors,t,si,sb,mn,formRef,aliases,onAddAlias,onRemoveAlias}){
const isBulk=mode==='bulk';

// --- Init ---
const bulkInit=useMemo(()=>{
  if(!isBulk)return null;
  const out={};
  for(const k of BULK_FIELDS){
    const vals=new Set((selectedIngs||[]).map(i=>{const v=i[k];return v!=null?String(v):'';}));
    out[k]=vals.size===1?[...vals][0]:MIXED;
  }
  out.n='';out.b='';out.vin='';out.err=null;
  // Compute uc only if all shape fields are non-mixed
  const shapeMixed=BULK_FIELDS.some(k=>SHAPE_KEYS.has(k)&&out[k]===MIXED)||out.cp===MIXED;
  out.uc=shapeMixed?'':computeUcFromState(out);
  return out;
},[isBulk,selectedIngs]);

const defaults={n:'',b:'',vin:'',cat:'',dist:vendors[0]||'',ec:'Food',ppc:'',spp:'',pt:'',cu:'EACH',cp:'',uc:'',pdu:'',srv:'',sru:'EACH',yp:'',pcu:'',err:null};
const init=isBulk?bulkInit:initial?{
  n:initial.n||'',b:initial.b||'',vin:initial.vin||'',cat:initial.cat||'Other',
  dist:initial.dist||'',ec:initial.ec||'Food',ppc:(initial.ppc||1).toString(),
  spp:(initial.spp||initial.cq||'').toString(),pt:initial.pt||'',cu:initial.cu||'EACH',
  cp:initial.cp!=null?initial.cp.toString():'',uc:'',pdu:initial.pdu||'',
  srv:initial.srv?String(initial.srv):'',sru:initial.sru||'EACH',yp:(initial.yp||100).toString(),
  pcu:initial.pcu||'',err:null
}:defaults;

// Compute initial uc for non-bulk modes
if(!isBulk&&init.cp){init.uc=computeUcFromState(init);}

const[f,setF]=useState(init);
if(formRef)formRef.current=f;
const[touched,setTouched]=useState(new Set());

// In bulk mode, check if any shape field is mixed (considering user edits)
const shapeMixed=isBulk&&BULK_FIELDS.some(k=>SHAPE_KEYS.has(k)&&f[k]===MIXED);

// Modified fields (bulk only)
const modified=useMemo(()=>{
  if(!isBulk)return new Set();
  const m=new Set();for(const k of touched){if(f[k]!==init[k])m.add(k);}return m;
},[isBulk,f,touched,init]);
const changeCount=modified.size;

// --- Field setters ---
function markTouched(k){if(isBulk)setTouched(p=>{const n=new Set(p);n.add(k);return n;});}

function onCpChange(val){
  markTouched('cp');
  setF(p=>{
    const cpN=parseFloat(val);if(!isFinite(cpN))return{...p,cp:val,err:null};
    if(isBulk&&shapeMixed)return{...p,cp:val,uc:'',err:null};
    const srv=parseFloat(p.srv)||0;const ppc=parseFloat(p.ppc)||1;
    const spp=parseFloat(p.spp)||0;const yp=parseFloat(p.yp)||100;
    let uc;
    if(srv>0){uc=cpN/srv*(100/yp);}
    else if(p.pdu&&DISCRETE.has(p.pdu)&&BULK_UNITS.has(p.cu)){uc=cpN/ppc*(100/yp);}
    else{const cq=ppc*spp;if(!cq||!yp)return{...p,cp:val,err:null};const base=cpN/cq*(100/yp);if(p.pdu&&p.pdu!==p.cu){const c=cv(1,p.pdu,p.cu);uc=c?base*c:base;}else uc=base;}
    return{...p,cp:val,uc:String(uc),err:null};
  });
}

function onUcChange(val){
  markTouched('uc');
  setF(p=>{
    const ucN=parseFloat(val);if(!isFinite(ucN))return{...p,uc:val,err:null};
    const srv=parseFloat(p.srv)||0;const ppc=parseFloat(p.ppc)||1;
    const spp=parseFloat(p.spp)||0;const yp=parseFloat(p.yp)||100;
    let cp;
    if(srv>0){cp=ucN*srv*yp/100;}
    else if(p.pdu&&DISCRETE.has(p.pdu)&&BULK_UNITS.has(p.cu)){cp=ucN*ppc*yp/100;}
    else{const cq=ppc*spp;if(!cq)return{...p,uc:val,err:null};let ucInBase=ucN;if(p.pdu&&p.pdu!==p.cu){const c=cv(1,p.pdu,p.cu);if(!c)return{...p,uc:val,err:null};ucInBase=ucN/c;}cp=ucInBase*cq*yp/100;}
    return{...p,uc:val,cp:String(cp),err:null};
  });
}

function handleSave(){
  if(isBulk){
    const changes={};
    for(const k of modified){
      if(k==='cp'||k==='yp'||k==='ppc'||k==='spp'||k==='srv'){if(f[k]!=='')changes[k]=f[k];}
      else changes[k]=f[k];
    }
    if(Object.keys(changes).length)onSave(changes);
    return;
  }
  const ppc=parseFloat(f.ppc)||1,spp=parseFloat(f.spp)||0,cq=ppc*spp;
  const cp=Math.round((parseFloat(f.cp)||0)*100)/100;
  const yp=parseFloat(f.yp);
  if(!f.n){setF(p=>({...p,err:'Name required'}));return;}
  if(!f.cat){setF(p=>({...p,err:'Category required'}));return;}
  if(!cq){setF(p=>({...p,err:'Invalid pack size'}));return;}
  if(!isFinite(cp)||cp<0){setF(p=>({...p,err:'Invalid price'}));return;}
  onSave({
    n:f.n.toUpperCase(),b:(f.b||'').toUpperCase(),vin:(f.vin||'').toUpperCase(),
    cat:f.cat,dist:(f.dist||'').toUpperCase(),ec:f.ec||'Food',
    ppc,spp,cq,cu:f.cu,cp,yp:isNaN(yp)?100:yp,
    pt:f.pt||null,pdu:f.pdu||null,
    srv:f.srv?parseInt(f.srv):null,sru:f.sru||null,slc:null,
    pcu:f.pcu||(f.sru||null)
  });
}

const set=(k,v)=>{
  markTouched(k);
  setF(p=>{
    const next={...p,[k]:v};
    if(!SHAPE_KEYS.has(k))return next;
    if(isBulk&&BULK_FIELDS.some(fk=>SHAPE_KEYS.has(fk)&&next[fk]===MIXED))return{...next,uc:''};
    const cpN=parseFloat(next.cp);if(!isFinite(cpN))return next;
    const srv=parseFloat(next.srv)||0;const ppc=parseFloat(next.ppc)||1;
    const spp=parseFloat(next.spp)||0;const yp=parseFloat(next.yp)||100;
    let uc;
    if(srv>0){uc=cpN/srv*(100/yp);}
    else if(next.pdu&&DISCRETE.has(next.pdu)&&BULK_UNITS.has(next.cu)){uc=cpN/ppc*(100/yp);}
    else{const cq=ppc*spp;if(!cq||!yp)return next;const base=cpN/cq*(100/yp);if(next.pdu&&next.pdu!==next.cu){const c=cv(1,next.pdu,next.cu);uc=c?base*c:base;}else uc=base;}
    return{...next,uc:String(uc)};
  });
};

// --- Rendering helpers ---
const modS=(k)=>isBulk&&modified.has(k)?{borderLeft:`3px solid ${t.bl}`}:{};
const isMixed=(k)=>isBulk&&init[k]===MIXED&&!touched.has(k);

const inp=(k,type,ph)=>type==='number'
  ?<input type="number" value={f[k]===MIXED?'':f[k]||''} onChange={e=>set(k,e.target.value)} placeholder={isMixed(k)?"(mixed)":ph||''} style={{...si,width:"100%",fontStyle:isMixed(k)?'italic':'normal',...modS(k)}}/>
  :<input value={f[k]||''} onChange={e=>set(k,e.target.value)} placeholder={ph||''} style={{...si,width:"100%",...modS(k)}}/>;

const sel=(k,opts,blank)=>{
  const mixed=isBulk&&init[k]===MIXED;
  const cur=f[k]||'';
  const isLegacy=cur&&!mixed&&!opts.includes(cur);
  return <select value={cur} onChange={e=>set(k,e.target.value)} style={{...si,width:"100%",fontStyle:f[k]===MIXED?'italic':'normal',...modS(k)}}>
    {mixed&&<option value={MIXED} disabled style={{fontStyle:'italic',color:'#999'}}>(mixed)</option>}
    {!mixed&&blank&&<option value="">{blank}</option>}
    {isLegacy&&<option value={cur} style={{fontStyle:'italic'}}>{cur}</option>}
    {opts.map(v=><option key={v} value={v}>{EC_LABELS[v]||v}</option>)}
  </select>;
};

const lbl=(text,child)=><div><label style={{fontSize:9,color:t.td}}>{text}</label>{child}</div>;

const distOpts=useMemo(()=>{
  const uc=[...new Set(vendors.map(v=>v.toUpperCase()))].sort();
  if(f.dist&&!uc.includes(f.dist.toUpperCase()))uc.unshift(f.dist);
  return uc;
},[vendors,f.dist]);
const ucDisabled=isBulk&&shapeMixed;
const bulkCount=isBulk?(selectedIngs||[]).length:0;

return(
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:16,marginBottom:16}}>

{/* Row 1: Identity — hidden in bulk mode */}
{!isBulk&&<div style={{display:"grid",gridTemplateColumns:"1fr 3fr 1.5fr",gap:8,marginBottom:8}}>
{lbl("Item #",inp("vin","text","e.g. 14150"))}
{lbl("Product",inp("n","text"))}
{lbl("Brand",inp("b","text"))}
</div>}

{/* Row 2: Classification */}
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
{lbl("Category",sel("cat",categories,"Select..."))}
{lbl("Distributor",sel("dist",distOpts))}
{lbl("Cost Category",sel("ec",EC_OPTS))}
</div>

{/* Row 3: How It Comes */}
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
{lbl("Packs/Case",inp("ppc","number","1"))}
{lbl("Pack Type",sel("pt",PT_OPTS,"—"))}
{lbl("Size/Pack",inp("spp","number","qty"))}
{lbl("Unit",sel("cu",UL))}
</div>

{/* Row 4: Pricing */}
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
{lbl("Case $",<DollarInput value={f.cp===MIXED?'':f.cp} onChange={v=>onCpChange(String(v))} placeholder={isMixed('cp')?"(mixed)":""} style={{...si,width:"100%",fontFamily:mn,fontStyle:isMixed('cp')?'italic':'normal',...modS('cp')}}/>)}
{lbl("Unit Cost",ucDisabled
  ?<div style={{...si,width:"100%",fontFamily:mn,color:'#999',fontSize:11,padding:"6px 8px",background:t.sf,borderRadius:4}}>Set pack details first</div>
  :<input type="number" value={f.uc||''} onChange={e=>onUcChange(e.target.value)} style={{...si,width:"100%",fontFamily:mn,color:t.gn}}/>
)}
{lbl("Price As",sel("pdu",PDU_OPTS,"auto"))}
</div>

{/* Row 5: Adjustments */}
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:8}}>
{lbl("Count per case",inp("srv","number"))}
{lbl("Count unit",sel("sru",[...new Set(['EACH','SLICE','SERVING','PIECE','DOZEN','OZ','LB','G','TSP','TBS','ML'].filter(u=>u!==f.cu))],"—"))}
{lbl("Yield %",<input type="text" inputMode="numeric" value={f.yp===MIXED?'':(f.yp||'')} onChange={e=>set('yp',e.target.value.replace(/[^0-9.]/g,''))} placeholder={isMixed('yp')?"(mixed)":"100"} style={{...si,width:"100%",fontStyle:isMixed('yp')?'italic':'normal',...modS('yp')}}/>)}
{lbl("Recipe Unit",sel("pcu",[...new Set(['','EACH','SLICE','SERVING','PIECE','DOZEN','OZ','LB','G','GAL','QUART','LITERS','ML','TBS','TSP'].filter(u=>u!==f.cu))],"auto"))}
</div>

{/* Aliases — edit mode only, when alias props provided */}
{mode==='edit'&&aliases&&onAddAlias&&onRemoveAlias&&<AliasSection aliases={aliases} onAdd={onAddAlias} onRemove={onRemoveAlias} t={t} si={si} sb={sb}/>}

{/* Actions */}
<div style={{display:"flex",gap:8,alignItems:"center"}}>
{isBulk
  ?<button onClick={handleSave} disabled={!changeCount} style={{...sb,background:changeCount?t.bl:"#888",color:"#fff",opacity:changeCount?1:0.5}}>{changeCount?`Apply ${changeCount} change${changeCount!==1?'s':''} to ${bulkCount} item${bulkCount!==1?'s':''}`:`Apply to ${bulkCount} item${bulkCount!==1?'s':''}`}</button>
  :<button onClick={handleSave} style={{...sb,background:t.gn}}>Save</button>
}
<button onClick={onCancel} style={{...sb,background:t.tm}}>Cancel</button>
{f.err&&<span style={{fontSize:12,color:t.ac,fontWeight:600}}>{f.err}</span>}
</div>

</div>);
}
